import { PaginationCardMinimal } from "@admin-ui/components/application/pagination/pagination";
import { DeleteEntityDialog } from "@admin-ui/components/composed/DeleteEntityDialog";
import { LifecycleBadge } from "@admin-ui/components/composed/LifecycleBadge";
import { ConfirmationDialog } from "@admin-ui/components/primitives/application/confirmation-dialog";
import { Table, TableCard } from "@admin-ui/components/primitives/application/table/table";
import { Badge } from "@admin-ui/components/primitives/base/badges/badges";
import { Dropdown } from "@admin-ui/components/primitives/base/dropdown/dropdown";
import { formatDate } from "@admin-ui/utils/format-date";
import { adminUserPath } from "@travily/routes";
import type { AdminUsersUser, AdminUsersUserFoundingMemberTrait } from "@travily/types/models";
import type AdminUsersIndex from "@travily/types/pages/AdminUsersIndex";
import { CheckCircle, MinusCircle, ShieldOff, ShieldTick, Star01, Trash01 } from "@untitledui/icons";
import { useState } from "react";
import { Button as AriaButton, Link, TableBody } from "react-aria-components";
import { FoundingMemberModal } from "./components";

type UserWithFoundingMember = AdminUsersUser & AdminUsersUserFoundingMemberTrait;

export interface IndexViewProps {
  users: UserWithFoundingMember[];
  currentUserId: number;
  pagy: AdminUsersIndex["pagy"];
  onActivateUser: (userId: number) => void;
  onDeactivateUser: (userId: number) => void;
  onPageChange: (page: number) => void;
  onToggleAdmin: (userId: number) => void;
  onDeleteUser: (userId: number, force: boolean) => void;
}

type PendingAction =
  | { type: "activate"; userId: number }
  | { type: "deactivate"; userId: number }
  | { type: "toggle-admin"; userId: number; isAdmin: boolean }
  | { type: "view-founding-member"; userId: number }
  | { type: "delete"; userId: number };

export function IndexView({
  users,
  currentUserId,
  pagy,
  onActivateUser,
  onDeactivateUser,
  onPageChange,
  onToggleAdmin,
  onDeleteUser,
}: IndexViewProps) {
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);

  return (
    <>
      <TableCard.Root>
        <Table aria-label="Users">
          <Table.Header>
            <Table.Head id="name" label="Name" isRowHeader />
            <Table.Head id="email" label="Email" />
            <Table.Head id="status" label="Status" />
            <Table.Head id="role" label="Role" />
            <Table.Head id="created" label="Created" />
            <Table.Head id="actions" label="" />
          </Table.Header>
          <TableBody items={users}>
            {(user: UserWithFoundingMember) => (
              <Table.Row key={user.id} id={user.id}>
                <Table.Cell>
                  <span className="flex items-center gap-1.5">
                    <Link
                      href={adminUserPath(user.id)}
                      className="text-sm font-medium text-brand-600 hover:text-brand-700"
                    >
                      {user.name ?? "-"}
                    </Link>
                    {user.founding_member && (
                      <AriaButton
                        aria-label="View founding member profile"
                        onPress={() => setPendingAction({ type: "view-founding-member", userId: user.id })}
                        className="cursor-pointer rounded p-0.5 text-utility-yellow-500 outline-hidden transition duration-100 ease-linear hover:text-utility-yellow-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
                      >
                        <Star01 className="size-4" />
                      </AriaButton>
                    )}
                  </span>
                </Table.Cell>
                <Table.Cell>{user.email}</Table.Cell>
                <Table.Cell>
                  <LifecycleBadge lifecycleState={user.lifecycle_state} anonymizedAt={user.anonymized_at} />
                </Table.Cell>
                <Table.Cell>
                  {user.admin ? (
                    <Badge color="purple" size="sm">
                      admin
                    </Badge>
                  ) : null}
                </Table.Cell>
                <Table.Cell>{formatDate(user.created_at)}</Table.Cell>
                <Table.Cell>
                  {user.id !== currentUserId && user.lifecycle_state !== "anonymized" && (
                    <UserActionsDropdown user={user} onAction={setPendingAction} />
                  )}
                </Table.Cell>
              </Table.Row>
            )}
          </TableBody>
        </Table>
      </TableCard.Root>

      <PaginationCardMinimal align="right" page={pagy.page} total={pagy.pages} onPageChange={onPageChange} />

      {pendingAction?.type === "activate" && (
        <ConfirmationDialog
          title="Admit user"
          description="This will admit the user from the waitlist and grant them full access to the platform."
          confirmLabel="Admit"
          isOpen
          onOpenChange={(open) => {
            if (!open) setPendingAction(null);
          }}
          onConfirm={() => {
            onActivateUser(pendingAction.userId);
            setPendingAction(null);
          }}
        />
      )}

      {pendingAction?.type === "deactivate" && (
        <ConfirmationDialog
          title="Waitlist user"
          description="This will move the user back to the waitlist. They will lose access to the platform."
          confirmLabel="Waitlist"
          color="warning"
          isOpen
          onOpenChange={(open) => {
            if (!open) setPendingAction(null);
          }}
          onConfirm={() => {
            onDeactivateUser(pendingAction.userId);
            setPendingAction(null);
          }}
        />
      )}

      {pendingAction?.type === "toggle-admin" && (
        <ConfirmationDialog
          title={pendingAction.isAdmin ? "Revoke admin" : "Grant admin"}
          description={
            pendingAction.isAdmin
              ? "This will remove admin privileges from this user. They will no longer be able to access the admin area."
              : "This will grant admin privileges to this user. They will be able to access the admin area and manage all users."
          }
          confirmLabel={pendingAction.isAdmin ? "Revoke" : "Grant"}
          isOpen
          onOpenChange={(open) => {
            if (!open) setPendingAction(null);
          }}
          onConfirm={() => {
            onToggleAdmin(pendingAction.userId);
            setPendingAction(null);
          }}
        />
      )}

      {pendingAction?.type === "view-founding-member" &&
        (() => {
          const user = users.find((u) => u.id === pendingAction.userId);
          if (!user?.founding_member) return null;
          return (
            <FoundingMemberModal
              isOpen
              onOpenChange={(open) => {
                if (!open) setPendingAction(null);
              }}
              userName={user.name}
              foundingMember={user.founding_member}
            />
          );
        })()}

      {pendingAction?.type === "delete" &&
        (() => {
          const user = users.find((u) => u.id === pendingAction.userId);
          if (!user) return null;
          return (
            <DeleteEntityDialog
              entityLabel="user"
              entityName={user.name ?? user.email}
              deletionEffect={user.deletion_effect}
              commerceCount={user.commerce_count}
              isOpen
              onOpenChange={(open) => {
                if (!open) setPendingAction(null);
              }}
              onConfirm={(force) => {
                onDeleteUser(pendingAction.userId, force);
                setPendingAction(null);
              }}
            />
          );
        })()}
    </>
  );
}

interface UserActionsDropdownProps {
  user: AdminUsersUser;
  onAction: (action: PendingAction) => void;
}

function UserActionsDropdown({ user, onAction }: UserActionsDropdownProps) {
  return (
    <Dropdown.Root>
      <Dropdown.DotsButton />
      <Dropdown.Popover className="w-min">
        <Dropdown.Menu
          onAction={(key) => {
            if (key === "activate") onAction({ type: "activate", userId: user.id });
            if (key === "deactivate") onAction({ type: "deactivate", userId: user.id });
            if (key === "toggle-admin") onAction({ type: "toggle-admin", userId: user.id, isAdmin: user.admin });
            if (key === "delete") onAction({ type: "delete", userId: user.id });
          }}
        >
          {user.status === "waitlisted" && <Dropdown.Item id="activate" icon={CheckCircle} label="Admit" />}
          {user.status === "active" && <Dropdown.Item id="deactivate" icon={MinusCircle} label="Waitlist" />}
          <Dropdown.Item
            id="toggle-admin"
            icon={user.admin ? ShieldOff : ShieldTick}
            label={user.admin ? "Revoke admin" : "Grant admin"}
          />
          <Dropdown.Item id="delete" icon={Trash01} label="Delete user" />
        </Dropdown.Menu>
      </Dropdown.Popover>
    </Dropdown.Root>
  );
}
